import { Box, Button, Typography, TextField } from "@mui/material";
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import Skeleton from "react-loading-skeleton";
import { doc, setDoc, updateDoc, Timestamp, onSnapshot } from "firebase/firestore";
import { DATABASE } from "../../../Config/firebase";
import { toast } from "react-toastify";

const PDFReportSettings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  
  useEffect(() => {
    const docRef = doc(DATABASE, "app_setting", "pdf_report_text");
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        setData(docSnap?.data()?.content);
      } else {
        // Set default text if document doesn't exist
        setData(`In alignment with our dedication to market analysis and transparency, FIDDUX is pleased to present its monthly performance report, which is based on extensive technical analysis and strategic positioning within the digital assets markets. Our committed team diligently continues to monitor and assess market trends to provide our clients with the best results and outcomes.`);
      }
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching PDF report text: ", error);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      report_content: data || "",
    },
    validationSchema: Yup.object({
      report_content: Yup.string().required("Report content is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setIsUpdating(true);
      try {
        const docRef = doc(DATABASE, "app_setting", "pdf_report_text");
        if (data !== null) {
          await updateDoc(docRef, {
            content: values?.report_content,
            doc_id: "pdf_report_text",
            type: 0,
            update_date: Timestamp.now(),
          });
        } else {
          await setDoc(docRef, {
            content: values?.report_content,
            doc_id: "pdf_report_text",
            type: 0,
            create_date: Timestamp.now(),
            update_date: Timestamp.now(),
          });
        }
        toast.success("PDF Report text updated successfully!");
        resetForm({ values: { report_content: values?.report_content } });
        setIsUpdating(false);
      } catch (error) {
        console.error("Error updating PDF report text: ", error);
        toast.error("Failed to update PDF report text.");
        setIsUpdating(false);
      }
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton height={400} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold" }}>
        PDF Report Settings
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
        Edit the content that appears in the monthly performance report PDFs sent to clients. 
        This text will be used for all future PDF reports unless changed.
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={8}
            id="report_content"
            name="report_content"
            label="Report Content"
            value={formik.values.report_content}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.report_content && Boolean(formik.errors.report_content)}
            helperText={formik.touched.report_content && formik.errors.report_content}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              },
            }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isUpdating}
            sx={{ minWidth: 120 }}
          >
            {isUpdating ? "Updating..." : "Save Changes"}
          </Button>
          
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={() => formik.resetForm({ values: { report_content: data } })}
            disabled={isUpdating}
          >
            Reset
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default PDFReportSettings;
